import { useCallback, useEffect, useRef, useState } from "react";
import { Film, Lock, Music, Play, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sel, inputCls } from "../components/Form";
import { errMsg, mediaUrl, useApi, loadFailed } from "../lib/api";
import { useMe } from "../lib/me";
import { PublishYouTube, YouTubeBadge } from "../components/library/YouTube";

export default function Library() {
  const api = useApi();
  const { me } = useMe();
  const [data, setData] = useState({ items: [], spaces: [], uploaders: [] });
  const [f, setF] = useState({ q: "", space_id: "", uploader_id: "", kind: "" });
  const [player, setPlayer] = useState(null);
  const [video, setVideo] = useState(null);
  const tmr = useRef(null);
  const load = useCallback(() => api.get("/library", { params: f }).then((r) => setData(r.data)).catch(loadFailed("the library")), [api, f]);
  useEffect(() => { clearTimeout(tmr.current); tmr.current = setTimeout(load, 250); }, [load]);
  const [yt, setYt] = useState(null);
  useEffect(() => { if (me.role === "admin") api.get("/youtube/status").then((r) => setYt(r.data)).catch(loadFailed("YouTube status")); }, [api, me.role]);
  const uploading = data.items.some((it) => it.youtube?.status === "uploading");
  useEffect(() => { if (!uploading) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [uploading, load]);

  const play = async (it) => {
    if (it.locked) return toast.error("Members only — join the camp to play this");
    try {
      const url = await mediaUrl(api, it.id);
      it.kind === "recording" ? setVideo({ ...it, url }) : setPlayer({ ...it, url });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (it, body) => { try { await api.patch(`/library/${it.id}`, body); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (it) => { try { await api.delete(`/library/${it.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="spotlight min-h-screen pb-28" data-testid="library-page">
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-10">
        <span className="mono-label text-yellow-300">Music library</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">The camp's recordings and tracks</h1>
        <div className="mt-8 grid gap-3 md:grid-cols-[1fr_200px_200px_160px]">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input className={`${inputCls} pl-9`} placeholder="Search titles" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="library-search-input" /></div>
          <Sel value={f.space_id} onChange={(v) => setF({ ...f, space_id: v })} testId="library-room-filter" placeholder="All rooms" options={data.spaces.map((s) => ({ value: s.id, label: s.name }))} />
          <Sel value={f.uploader_id} onChange={(v) => setF({ ...f, uploader_id: v })} testId="library-teacher-filter" placeholder="Everyone" options={data.uploaders.map((u) => ({ value: u.id, label: u.name }))} />
          <Sel value={f.kind} onChange={(v) => setF({ ...f, kind: v })} testId="library-type-filter" placeholder="All types" options={[{ value: "recording", label: "Recordings" }, { value: "track", label: "Tracks" }]} />
        </div>
        <div className="mt-6 panel divide-y divide-[#2d2d38]">
          {data.items.length === 0 && <p className="p-6 text-sm text-zinc-500" data-testid="library-empty">Nothing here yet. Upload tracks in a room, or record a performance.</p>}
          {data.items.map((it) => {
            const mine = me.role === "admin" || it.uploader_id === me.user_id;
            return (
              <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`library-item-${it.id}`}>
                <button onClick={() => play(it)} data-testid={`library-play-btn-${it.id}`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-zinc-700 hover:border-[#39ff6a] hover:text-[#39ff6a] transition-colors">
                  {it.locked ? <Lock className="h-4 w-4 text-zinc-500" /> : <Play className="h-4 w-4" />}
                </button>
                {it.kind === "recording" ? <Film className="h-4 w-4 text-pink-400 shrink-0" /> : <Music className="h-4 w-4 text-yellow-400 shrink-0" />}
                <div className="min-w-0 flex-1"><p className="font-semibold truncate">{it.title}</p>
                  <p className="mono text-[11px] text-zinc-400">{it.space_name} · {it.uploader_name} · {new Date(it.created_at).toLocaleDateString()}</p></div>
                <YouTubeBadge yt={it.youtube} />
                {me.role === "admin" && it.kind === "recording" && <PublishYouTube item={it} status={yt} onDone={load} />}
                {mine ? (
                  <select value={it.visibility} onChange={(e) => patch(it, { visibility: e.target.value })} data-testid={`library-visibility-${it.id}`} className="rounded-[3px] bg-black border border-zinc-800 text-xs px-2 py-1.5">
                    <option value="public">Public</option><option value="members">Members only</option>
                  </select>
                ) : <span className={`mono text-[10px] uppercase ${it.visibility === "public" ? "text-zinc-500" : "text-yellow-300"}`}>{it.visibility === "public" ? "Public" : "Members"}</span>}
                {mine && <button onClick={() => remove(it)} data-testid={`library-delete-btn-${it.id}`} className="text-zinc-500 hover:text-pink-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
      </div>
      {player && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#2d2d38] bg-[#0a0a0c]/95 backdrop-blur-xl" data-testid="library-player">
          <div className="mx-auto max-w-6xl flex items-center gap-4 px-4 py-3">
            <span className="h-2 w-2 rounded-full bg-[#39ff6a] glow-green" />
            <span className="font-semibold truncate max-w-[30%]">{player.title}</span>
            <audio src={player.url} controls autoPlay className="flex-1" data-testid="library-audio" />
            <button onClick={() => setPlayer(null)} data-testid="library-player-close" className="text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <Dialog open={!!video} onOpenChange={() => setVideo(null)}>
        <DialogContent className="bg-[#141419] border-[#2d2d38] max-w-4xl" data-testid="library-video-dialog">
          <DialogHeader><DialogTitle>{video?.title}</DialogTitle></DialogHeader>
          {video && <video src={video.url} controls autoPlay className="w-full rounded-md bg-black" data-testid="library-video" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
